/**
 * Meeting List Page
 * ==================
 * Top-level page listing all meetings with filter bar (search, status, platform),
 * and the ability to link orphan meetings to leads.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardBody, Button, Badge, FilterDropdown, Alert, Modal, Input } from '../../components/ui';
import { DashboardLayout } from '../../components/layout';
import meetingAPI from '../../api/meetings';
import { leadAPI, organizationAPI } from '../../api';
import { useToast, usePermissions, useOrg } from '../../hooks';
import { nowLocalDateTime } from '../../utils/datetime';

const MeetingListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { is, isOrgAdmin } = usePermissions();
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.id;
  const canScheduleMeeting = !is.viewer() && !is.complianceOfficer();

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Create Meeting Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [meetingMode, setMeetingMode] = useState('discovery'); // 'discovery' or 'lead'
  const [selectedLeadForMeeting, setSelectedLeadForMeeting] = useState(null);
  const [meetingLeadSearch, setMeetingLeadSearch] = useState('');
  const [meetingLeadResults, setMeetingLeadResults] = useState([]);
  const [meetingLeadSearchLoading, setMeetingLeadSearchLoading] = useState(false);
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [gmStatus, setGmStatus] = useState(null);

  // Add Bot to Live Meeting (Recall.ai) — org-admin only, shown when Recall is enabled
  const [recallEnabled, setRecallEnabled] = useState(false);
  const [recallHasKey, setRecallHasKey] = useState(false);
  const [botModalOpen, setBotModalOpen] = useState(false);
  const [botUrl, setBotUrl] = useState('');
  const [sendingBot, setSendingBot] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    type: 'discovery',
    scheduled_at: '',
    duration_minutes: 60,
    platform: 'google_meet',
    attendees: [],
    agenda: '',
    meeting_link: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [attendeeInput, setAttendeeInput] = useState('');

  // Local search input for debounce
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const statusFilter = searchParams.has('status') ? searchParams.get('status') : 'unlinked';
  const platformFilter = searchParams.get('platform') || '';
  const searchQuery = searchParams.get('search') || '';

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = {
        page: currentPage,
        limit: pagination.limit,
      };
      if (statusFilter === 'unlinked') {
        params.unlinked = true;
      } else if (statusFilter === 'linked') {
        params.unlinked = false;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await meetingAPI.listMeetings(params);
      if (response.data) {
        let results = response.data.meetings || [];
        // Client-side platform filter (not supported by backend)
        if (platformFilter) {
          results = results.filter((m) => m.platform === platformFilter);
        }
        setMeetings(results);
        setPagination(response.data.pagination || pagination);
      }
    } catch (err) {
      console.error('Error fetching meetings:', err);
      setFetchError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, platformFilter, searchQuery, pagination.limit]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Debounce search input → URL param
  useEffect(() => {
    const timer = setTimeout(() => {
      const current = searchParams.get('search') || '';
      if (searchInput !== current) {
        const params = new URLSearchParams(searchParams);
        if (searchInput) {
          params.set('search', searchInput);
        } else {
          params.delete('search');
        }
        params.delete('page');
        setSearchParams(params);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lead search for create meeting modal
  useEffect(() => {
    if (!meetingLeadSearch || meetingLeadSearch.length < 2) {
      setMeetingLeadResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setMeetingLeadSearchLoading(true);
      try {
        const response = await leadAPI.listLeads({ search: meetingLeadSearch, limit: 10 });
        if (response.data && response.data.leads) {
          setMeetingLeadResults(response.data.leads);
        }
      } catch (err) {
        console.error('Error searching leads:', err);
      } finally {
        setMeetingLeadSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [meetingLeadSearch]);

  const handleFilterChange = (key, value) => {
    const params = new URLSearchParams(searchParams);
    // Always keep status in URL so the default 'unlinked' can be overridden
    if (key === 'status') {
      params.set(key, value);
    } else if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleRowClick = (meeting) => {
    if (meeting.lead_id) {
      navigate(`/leads/${meeting.lead_id}/meetings/${meeting.id}`);
    } else {
      navigate(`/meetings/${meeting.id}`);
    }
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
    // Fetch Google Meet readiness so we can gate the create button before submit.
    meetingAPI
      .getGoogleMeetStatus()
      .then(setGmStatus)
      .catch(() => { /* status unavailable — leave gate open, backend still enforces */ });
    setMeetingMode('discovery');
    setSelectedLeadForMeeting(null);
    setMeetingLeadSearch('');
    setMeetingLeadResults([]);
    setMeetingForm({
      title: '',
      type: 'discovery',
      scheduled_at: '',
      duration_minutes: 60,
      platform: 'google_meet',
      attendees: [],
      agenda: '',
      meeting_link: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    setAttendeeInput('');
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  // Load Recall.ai readiness so we only surface "Add Bot" for orgs that use it.
  // Config is org-admin gated (same as the send-bot endpoint), so skip for others.
  useEffect(() => {
    if (!isOrgAdmin || !orgId) return;
    organizationAPI
      .getRecallConfig(orgId)
      .then((cfg) => {
        setRecallEnabled(!!cfg.enabled);
        setRecallHasKey(!!cfg.api_key);
      })
      .catch(() => { /* config unavailable — leave the button hidden */ });
  }, [isOrgAdmin, orgId]);

  const openBotModal = () => {
    setBotUrl('');
    setBotModalOpen(true);
  };

  const handleSendBot = async () => {
    const url = botUrl.trim();
    // Client-side guard only — real platform validation stays server-side.
    if (!url || !url.startsWith('https://')) {
      showToast('Enter a valid meeting URL (must start with https://)', 'error');
      return;
    }
    setSendingBot(true);
    try {
      await organizationAPI.sendRecallBot(orgId, { meeting_url: url });
      showToast('Bot dispatched to meeting', 'success');
      setBotModalOpen(false);
      setBotUrl('');
    } catch (err) {
      const msg = err?.response?.data?.error?.message || 'Failed to dispatch bot to meeting';
      showToast(msg, 'error');
    } finally {
      setSendingBot(false);
    }
  };

  const handleMeetingFormChange = (e) => {
    const { name, value } = e.target;
    setMeetingForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAttendee = () => {
    if (attendeeInput && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendeeInput)) {
      setMeetingForm((prev) => ({
        ...prev,
        attendees: [...prev.attendees, attendeeInput],
      }));
      setAttendeeInput('');
    }
  };

  const handleRemoveAttendee = (email) => {
    setMeetingForm((prev) => ({
      ...prev,
      attendees: prev.attendees.filter((a) => a !== email),
    }));
  };

  const handleModeChange = (mode) => {
    setMeetingMode(mode);
    if (mode === 'discovery') {
      setSelectedLeadForMeeting(null);
      setMeetingForm((prev) => ({
        ...prev,
        title: prev.title || 'Discovery Call',
        type: 'discovery',
      }));
    } else {
      setMeetingForm((prev) => ({
        ...prev,
        title: selectedLeadForMeeting ? `Discovery Call - ${selectedLeadForMeeting.company || selectedLeadForMeeting.name}` : prev.title,
      }));
    }
  };

  const handleCreateMeeting = async () => {
    if (!meetingForm.title.trim()) {
      showToast('Meeting title is required', 'error');
      return;
    }
    if (!meetingForm.scheduled_at) {
      showToast('Date and time is required', 'error');
      return;
    }
    if (new Date(meetingForm.scheduled_at) <= new Date()) {
      showToast('Meeting must be scheduled in the future', 'error');
      return;
    }
    if (meetingMode === 'lead' && !selectedLeadForMeeting) {
      showToast('Please select a lead', 'error');
      return;
    }

    setCreatingMeeting(true);
    try {
      const localDateTime = new Date(meetingForm.scheduled_at);
      const payload = {
        ...meetingForm,
        scheduled_at: localDateTime.toISOString(),
        lead_id: meetingMode === 'lead' ? selectedLeadForMeeting.id : undefined,
      };
      // Remove meeting_link if empty
      if (!payload.meeting_link) delete payload.meeting_link;

      const response = await meetingAPI.createMeeting(payload);
      if (response.data) {
        showToast(response.data.message || 'Meeting created successfully', 'success');
        handleCloseCreateModal();
        fetchMeetings();
      }
    } catch (err) {
      console.error('Error creating meeting:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to create meeting';
      showToast(errorMessage, 'error');
    } finally {
      setCreatingMeeting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getPlatformLabel = (platform) => {
    const labels = {
      google_meet: 'Google Meet',
      zoom: 'Zoom',
      teams: 'Teams',
      other: 'Other'
    };
    return labels[platform] || platform || 'N/A';
  };

  const getTranscriptBadge = (status) => {
    const variants = {
      completed: 'success',
      processing: 'warning',
      pending: 'warning',
      failed: 'danger',
      none: 'default'
    };
    const labels = {
      completed: 'Completed',
      processing: 'Processing',
      pending: 'Pending',
      failed: 'Failed',
      none: 'None'
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  // Rendered in both the mobile card list and the desktop table.
  const getClassificationBadge = (classification) => (
    <Badge variant={
      classification === 'new_lead' ? 'success' :
      classification === 'knowledge_update' ? 'info' :
      classification === 'structure_data_update' ? 'warning' : 'default'
    }>
      {classification === 'new_lead' ? 'New Lead' :
       classification === 'knowledge_update' ? 'Knowledge' :
       classification === 'structure_data_update' ? 'Structure' : classification}
    </Badge>
  );

  const getEmptyMessage = () => {
    if (searchQuery || platformFilter) {
      return 'No meetings match the current filters.';
    }
    if (statusFilter === 'unlinked') {
      return 'No discovery calls found.';
    }
    if (statusFilter === 'linked') {
      return 'No linked meetings found.';
    }
    return 'Meetings will appear here when scheduled or recorded.';
  };

  // Google Meet readiness gate for the create modal (P2). Only relevant when the
  // selected platform is Google Meet; other platforms are unaffected.
  const gmIsGoogleMeet = meetingForm.platform === 'google_meet';
  const gmReady = gmStatus && gmStatus.connected && gmStatus.enabled;
  const gmBlocked = gmIsGoogleMeet && gmStatus && !gmReady;
  const gmWarning = !gmBlocked
    ? null
    : !gmStatus.connected
      ? 'No Google account is connected for this organization. Connect one under Organization Settings → Meetings & Integrations before scheduling a Google Meet.'
      : 'Google Meet is connected but disabled for this organization. Enable it under Organization Settings → Meetings & Integrations before scheduling a Google Meet.';

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-[28px] font-sans font-bold text-gray-900 dark:text-white mb-2">
              Meetings
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              View all meetings, discovery calls, and link recordings to leads
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-shrink-0">
            {isOrgAdmin && recallEnabled && (
              <Button
                variant="secondary"
                onClick={openBotModal}
                disabled={!recallHasKey}
                title={recallHasKey ? "Dispatch this org's bot into a live meeting" : 'Configure a Recall API key in Organization Settings first'}
                className="w-full sm:w-auto justify-center"
              >
                Add Transcriber
              </Button>
            )}
            {canScheduleMeeting && (
              <Button variant="primary" onClick={handleOpenCreateModal} className="w-full sm:w-auto justify-center">
                + New Meeting
              </Button>
            )}
          </div>
        </div>

        {/* Meetings List */}
        <Card>
          <CardHeader>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:items-center">
              {/* Search */}
              <div className="relative col-span-2">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by title or company..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-[#3A3C3E] rounded-lg bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
                />
              </div>

              {/* Status Filter */}
              <FilterDropdown
                value={statusFilter}
                onChange={(val) => handleFilterChange('status', val)}
                options={[
                  { value: '', label: 'All Meetings' },
                  { value: 'unlinked', label: 'Discovery Calls' },
                  { value: 'linked', label: 'Linked Only' },
                ]}
              />

              {/* Platform Filter */}
              <FilterDropdown
                value={platformFilter}
                onChange={(val) => handleFilterChange('platform', val)}
                options={[
                  { value: '', label: 'All Platforms' },
                  { value: 'google_meet', label: 'Google Meet' },
                  { value: 'other', label: 'Other' },
                ]}
              />

              {/* Count */}
              <div className="col-span-2 lg:col-span-1 text-right">
                <span className="text-sm text-gray-500">
                  {pagination.total} meeting{pagination.total !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {fetchError ? (
              <div className="py-8 px-4">
                <Alert
                  variant={fetchError.code === 'NETWORK_ERROR' ? 'info' : 'error'}
                  title={fetchError.code === 'NETWORK_ERROR' ? 'Connection Issue' : 'Failed to Load Meetings'}
                >
                  {fetchError.message || 'An unexpected error occurred.'}
                </Alert>
                <div className="mt-4 text-center">
                  <Button onClick={fetchMeetings}>Retry</Button>
                </div>
              </div>
            ) : loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading meetings...</p>
              </div>
            ) : meetings.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No meetings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {getEmptyMessage()}
                </p>
              </div>
            ) : (
              <>
              {/* Mobile card list */}
              <div className="md:hidden space-y-3">
                {meetings.map((meeting, index) => (
                  <div
                    key={meeting.id || index}
                    onClick={() => handleRowClick(meeting)}
                    className="cursor-pointer rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242628] p-4 active:bg-gray-50 dark:active:bg-[#2E3033]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {meeting.title || 'Untitled Meeting'}
                        </div>
                      </div>
                      {getTranscriptBadge(meeting.transcript_status)}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                      <span>{formatDate(meeting.scheduled_at)}</span>
                      <span>{formatDuration(meeting.duration_minutes)}</span>
                      <span>{getPlatformLabel(meeting.platform)}</span>
                      {!meeting.lead_id && meeting.classification && getClassificationBadge(meeting.classification)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <table className="hidden md:table w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="w-[34%] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="w-[20%] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Platform
                      </th>
                      <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Transcript
                      </th>
                      <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Classification
                      </th>
                      <th className="w-[10%] px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {meetings.map((meeting, index) => (
                      <tr
                        key={meeting.id || index}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                        onClick={() => handleRowClick(meeting)}
                      >
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {meeting.title || 'Untitled Meeting'}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(meeting.scheduled_at)}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {formatDuration(meeting.duration_minutes)}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {getPlatformLabel(meeting.platform)}
                        </td>
                        <td className="px-4 py-4">
                          {getTranscriptBadge(meeting.transcript_status)}
                        </td>
                        <td className="px-4 py-4">
                          {!meeting.lead_id && meeting.classification ? (
                            getClassificationBadge(meeting.classification)
                          ) : !meeting.lead_id ? (
                            <span className="text-xs text-gray-400">Pending</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleRowClick(meeting); }}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Showing page {currentPage} of {pagination.pages} ({pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Create Meeting Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
              onClick={handleCloseCreateModal}
            />
            <div className="relative bg-white dark:bg-[#1E2022] rounded-xl shadow-xl w-full max-w-lg p-6 z-10 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                New Meeting
              </h3>

              {/* Mode Selector */}
              <div className="flex gap-2 mb-5">
                <button
                  type="button"
                  onClick={() => handleModeChange('discovery')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                    meetingMode === 'discovery'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-[#242628] text-gray-700 dark:text-gray-300 border-gray-300 dark:border-[#3A3C3E] hover:bg-gray-50 dark:hover:bg-[#2A2C2E]'
                  }`}
                >
                  Discovery Call
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('lead')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                    meetingMode === 'lead'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-[#242628] text-gray-700 dark:text-gray-300 border-gray-300 dark:border-[#3A3C3E] hover:bg-gray-50 dark:hover:bg-[#2A2C2E]'
                  }`}
                >
                  Lead Meeting
                </button>
              </div>

              {/* Lead Selector (only for Lead Meeting mode) */}
              {meetingMode === 'lead' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Lead *</label>
                  {selectedLeadForMeeting ? (
                    <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{selectedLeadForMeeting.name}</div>
                        <div className="text-xs text-gray-500">{selectedLeadForMeeting.company}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedLeadForMeeting(null);
                          setMeetingLeadSearch('');
                        }}
                        className="text-gray-400 hover:text-red-500 ml-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        placeholder="Search leads by name or company..."
                        value={meetingLeadSearch}
                        onChange={(e) => setMeetingLeadSearch(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
                      />
                      {(meetingLeadResults.length > 0 || meetingLeadSearchLoading) && (
                        <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 dark:border-[#3A3C3E] rounded-lg">
                          {meetingLeadSearchLoading ? (
                            <div className="p-3 text-center text-sm text-gray-500">Searching...</div>
                          ) : (
                            meetingLeadResults.map((lead) => (
                              <button
                                key={lead.id}
                                type="button"
                                onClick={() => {
                                  setSelectedLeadForMeeting(lead);
                                  setMeetingLeadSearch('');
                                  setMeetingLeadResults([]);
                                  setMeetingForm((prev) => ({
                                    ...prev,
                                    title: prev.title || `Discovery Call - ${lead.company || lead.name}`,
                                  }));
                                }}
                                className="w-full text-left px-3 py-2 border-b border-gray-100 dark:border-[#3A3C3E] last:border-b-0 hover:bg-gray-50 dark:hover:bg-[#2A2C2E] transition-colors"
                              >
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{lead.name}</div>
                                <div className="text-xs text-gray-500">{lead.company}</div>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Meeting Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={meetingForm.title}
                    onChange={handleMeetingFormChange}
                    placeholder="Meeting title"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                    <select
                      name="type"
                      value={meetingForm.type}
                      onChange={handleMeetingFormChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
                    >
                      <option value="discovery">Discovery Call</option>
                      <option value="follow_up">Follow-up</option>
                      <option value="presentation">Presentation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platform</label>
                    <select
                      name="platform"
                      value={meetingForm.platform}
                      onChange={handleMeetingFormChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
                    >
                      <option value="google_meet">Google Meet</option>
                      <option value="zoom" disabled>Zoom (Coming Soon)</option>
                      <option value="teams" disabled>Microsoft Teams (Coming Soon)</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {meetingForm.platform === 'other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meeting Link *</label>
                    <input
                      type="url"
                      name="meeting_link"
                      value={meetingForm.meeting_link}
                      onChange={handleMeetingFormChange}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date & Time *</label>
                    <input
                      type="datetime-local"
                      name="scheduled_at"
                      value={meetingForm.scheduled_at}
                      onChange={handleMeetingFormChange}
                      min={nowLocalDateTime()}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Duration (min)</label>
                    <input
                      type="number"
                      name="duration_minutes"
                      value={meetingForm.duration_minutes}
                      onChange={handleMeetingFormChange}
                      min="15"
                      max="480"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
                    />
                  </div>
                </div>

                {/* Attendees */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attendees</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={attendeeInput}
                      onChange={(e) => setAttendeeInput(e.target.value)}
                      placeholder="attendee@email.com"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddAttendee();
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={handleAddAttendee}>
                      Add
                    </Button>
                  </div>
                  {meetingForm.attendees.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {meetingForm.attendees.map((email, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 bg-gray-100 dark:bg-[#2A2C2E] px-2 py-1 rounded-full text-xs text-gray-700 dark:text-gray-300"
                        >
                          {email}
                          <button type="button" onClick={() => handleRemoveAttendee(email)} className="text-gray-400 hover:text-red-500">
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Agenda</label>
                  <textarea
                    name="agenda"
                    value={meetingForm.agenda}
                    onChange={handleMeetingFormChange}
                    rows={3}
                    placeholder="Meeting agenda..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary resize-none"
                  />
                </div>
              </div>

              {gmWarning && (
                <div className="mt-4">
                  <Alert variant="warning">{gmWarning}</Alert>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-gray-200 dark:border-[#3A3C3E]">
                <Button variant="outline" onClick={handleCloseCreateModal} disabled={creatingMeeting}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateMeeting}
                  disabled={creatingMeeting || gmBlocked}
                >
                  {creatingMeeting ? 'Creating...' : meetingMode === 'discovery' ? 'Schedule Discovery Call' : 'Schedule Meeting'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={botModalOpen}
        onClose={() => !sendingBot && setBotModalOpen(false)}
        title="Add Transcriber to Live Meeting"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setBotModalOpen(false)} disabled={sendingBot}>Cancel</Button>
            <Button onClick={handleSendBot} disabled={sendingBot}>{sendingBot ? 'Sending…' : 'Send Bot'}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meeting URL</label>
            <Input
              type="url"
              autoFocus
              placeholder="https://meet.google.com/abc-defg-hij"
              value={botUrl}
              onChange={(e) => setBotUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !sendingBot) handleSendBot(); }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The transcriber joins immediately and may need to be admitted from the waiting room.
            The transcript appears in this organization once the meeting ends.
          </p>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default MeetingListPage;
